import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import { getAllRoutes, getAllRoutesByCompany } from "../../services";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import DateRangePicker, {
  WITHIN_A_MONTH_PRESET_GROUPS,
} from "../common/DateRangePicker";
import FilterField, {
  FILTER_ROW_BOTTOM_SPACING,
  FLOATING_HELPER_TEXT_SX,
} from "../SaleDepositReport/FilterField";
import { ALL_OPTION } from "./utils";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

const ALL_LABEL = "ทั้งหมด";

interface SalesReportFilterProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChangeDateRange: (from: Dayjs, to: Dayjs) => void;
  selectedRoute: string;
  setSelectedRoute: Dispatch<SetStateAction<string>>;
  selectedBranch: string;
  setSelectedBranch: Dispatch<SetStateAction<string>>;
  branches: Branch[];
}

export default function SalesReportFilter({
  dateFrom,
  dateTo,
  onChangeDateRange,
  selectedRoute,
  setSelectedRoute,
  selectedBranch,
  setSelectedBranch,
  branches,
}: Readonly<SalesReportFilterProps>) {
  const { isHeadOfficeAccounting } = useUserRole();
  const [saleRoutes, setSaleRoutes] = useState<SaleRoute[]>([]);

  const getRoutes = useCallback(async () => {
    if (isHeadOfficeAccounting) {
      if (selectedBranch === ALL_OPTION) {
        setSaleRoutes([]);
        return;
      }
      const {
        data: { data },
      } = await getAllRoutes(selectedBranch);
      setSaleRoutes(Array.isArray(data) ? data : data.rows ?? []);
    } else {
      const {
        data: { data },
      } = await getAllRoutesByCompany();
      setSaleRoutes(data);
    }
  }, [isHeadOfficeAccounting, selectedBranch]);

  useEffect(() => {
    getRoutes();
  }, [getRoutes]);

  // A route belongs to one branch: one left picked from the previous branch would export nothing.
  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedBranch(event.target.value);
    setSelectedRoute(ALL_OPTION);
  };

  const handleChangeRoute = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedRoute(event.target.value);
  };

  // เลือกสาขา "ทั้งหมด" = ดึงทุกสายส่งของทุกสาขา จึงไม่ต้องให้เลือกสายส่งรายตัว
  const isAllBranches = isHeadOfficeAccounting && selectedBranch === ALL_OPTION;

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="วันที่">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onChangeDateRange}
          // Only shortcuts that always fit in one export (MAX_EXPORT_RANGE_DAYS); a quarter or a
          // year would mostly pick a range too long to download.
          presetGroups={WITHIN_A_MONTH_PRESET_GROUPS}
        />
      </FilterField>

      {isHeadOfficeAccounting && (
        <FilterField label="สาขา">
          <TextField
            select
            fullWidth
            size="small"
            value={selectedBranch}
            onChange={handleChangeBranch}
          >
            <MenuItem value={ALL_OPTION}>{ALL_LABEL}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.company_id}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}

      <FilterField label="สายส่ง">
        <TextField
          select
          fullWidth
          size="small"
          disabled={isAllBranches}
          value={selectedRoute}
          onChange={handleChangeRoute}
          helperText={isAllBranches ? "ทุกสายส่งของทุกสาขา" : undefined}
          sx={FLOATING_HELPER_TEXT_SX}
        >
          <MenuItem value={ALL_OPTION}>{ALL_LABEL}</MenuItem>
          {saleRoutes.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
